// @ts-nocheck
// client/src/v2/estudos-global.tsx
// ────────────────────────────────────────────────────────────────────────
// Estudos — o acervo inteiro, de todos os livros pessoais.
//
// Isto NAO e uma segunda base de anotacoes: e a mesma `book_notes` lida de
// outro angulo. O card e o mesmo componente da tela por livro (NoteCard
// importado, nao copiado), e editar/apagar chama os mesmos PATCH/DELETE de
// /api/v2/notes/:id. Por isso os dois lados nunca divergem — apagar aqui
// apaga la, e vice-versa, sem nenhuma sincronizacao explicita.
//
// Clubes ficam de fora (decisao do Wesley): nota de clube pertence a conversa
// do grupo, nao ao acervo pessoal. O backend ja filtra por from_club_id.
//
// A ordem e cronologica, mais recente primeiro — diferente da tela por livro,
// que ordena por pagina. Aqui nao existe "pagina 30" comum entre livros
// distintos; o que liga tudo e QUANDO a pessoa anotou.
// ────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';
import { api } from '../services/apiV2';
// Direto do note-card: importar de book-study arrastava o BookStudyScreen
// inteiro (composer, camera, gravador) para o bundle desta tela.
import { NoteCard, FILTERS, semAcento } from './note-card';
import { AuthSession } from '../types';

const PERIODOS = [
  { key: 'tudo', label: 'Sempre' },
  { key: '7', label: '7 dias' },
  { key: '30', label: '30 dias' },
  { key: '90', label: '3 meses' },
];

export const EstudosGlobalScreen: React.FC<{
  session: AuthSession | null;
  onBack: () => void;
  /** Abre a tela de estudos daquele livro, ancorada na anotação. */
  onAbrirLivro?: (bookId: string, page: number | null) => void;
}> = ({ session, onBack, onAbrirLivro }) => {
  const [notas, setNotas] = useState<any[] | null>(null);
  const [livros, setLivros] = useState<any[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const [filtro, setFiltro] = useState('tudo');
  const [livroId, setLivroId] = useState<string>('todos');
  const [periodo, setPeriodo] = useState('tudo');
  const [busca, setBusca] = useState('');
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [sheetLivros, setSheetLivros] = useState(false);

  const [editando, setEditando] = useState<any>(null);
  const [rascunho, setRascunho] = useState('');
  const [salvando, setSalvando] = useState(false);
  const areaRef = useRef<any>(null);

  const carregar = async () => {
    try {
      const r = await api.listAllNotes(session);
      setNotas(r?.notes || []);
      setLivros(r?.books || []);
    } catch (e: any) {
      setErro(e?.message || 'Não consegui carregar suas anotações.');
      setNotas([]);
    }
  };
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [session]);

  useEffect(() => {
    if (editando && areaRef.current) areaRef.current.focus();
  }, [editando]);

  // ── a lista visível ───────────────────────────────────────────────────
  const lista = useMemo(() => {
    const todas = notas || [];
    const q = busca.trim().toLowerCase();
    const corte = periodo === 'tudo' ? null
      : Date.now() - Number(periodo) * 86400000;

    return todas
      .filter((n) => (filtro === 'tudo' ? true : filtro === 'foto' ? !!n.photo : n.type === filtro))
      .filter((n) => livroId === 'todos' || n.bookId === livroId)
      .filter((n) => !corte || new Date(n.createdAt).getTime() >= corte)
      // A busca varre o texto E o titulo do livro: quem procura "atomicos"
      // geralmente quer as anotacoes daquele livro, nao a palavra no meio de
      // uma frase. E agora ISSO E VERDADE: ate 24/09/2026 este comentario
      // prometia o exemplo que nao funcionava — so havia toLowerCase(), sem
      // normalizar acento, entao "atomicos" nao achava "Habitos Atomicos".
      .filter((n) => !q
        || semAcento(n.text).includes(q)
        || semAcento(n.bookTitle).includes(q))
      .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  }, [notas, filtro, livroId, periodo, busca]);

  // Agrupa por dia. Numa lista que mistura livros, a data e a unica ancora
  // que a pessoa tem para se localizar.
  const grupos = useMemo(() => {
    const out: Array<{ dia: string; itens: any[] }> = [];
    lista.forEach((n) => {
      const d = new Date(n.createdAt);
      const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
      const dn = new Date(d); dn.setHours(0, 0, 0, 0);
      const dif = Math.round((hoje.getTime() - dn.getTime()) / 86400000);
      const dia = dif === 0 ? 'Hoje' : dif === 1 ? 'Ontem'
        : d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: dn.getFullYear() !== hoje.getFullYear() ? 'numeric' : undefined });
      const ultimo = out[out.length - 1];
      if (ultimo && ultimo.dia === dia) ultimo.itens.push(n);
      else out.push({ dia, itens: [n] });
    });
    return out;
  }, [lista]);

  const salvarEdicao = async () => {
    if (!editando || salvando) return;
    setSalvando(true);
    try {
      await api.updateBookNote(session, editando.id, { text: rascunho });
      setNotas((prev) => (prev || []).map((n) => n.id === editando.id ? { ...n, text: rascunho } : n));
      setEditando(null);
    } catch (e: any) {
      setErro(e?.message || 'Não consegui salvar.');
    } finally { setSalvando(false); }
  };

  const apagar = async () => {
    if (!editando || salvando) return;
    setSalvando(true);
    try {
      await api.deleteBookNote(session, editando.id);
      // Some da lista e do contador do filtro. Como o DELETE e o mesmo da tela
      // por livro, ela ja nasce sem esta nota na proxima abertura.
      setNotas((prev) => (prev || []).filter((n) => n.id !== editando.id));
      setLivros((prev) => prev.map((b) => b.id === editando.bookId
        ? { ...b, total: Math.max(0, (b.total || 1) - 1) } : b).filter((b) => b.total > 0));
      setEditando(null);
    } catch (e: any) {
      setErro(e?.message || 'Não consegui apagar.');
    } finally { setSalvando(false); }
  };

  const total = (notas || []).length;
  const livroAtivo = livros.find((b) => b.id === livroId);

  // Os filtros nascem do que EXISTE no acervo, nao de uma lista fixa. Numa
  // conta que so tem nota, foto e audio, oferecer "Grifos" e "Perguntas" e
  // prometer um recorte que devolve nada — e foi exatamente o que apareceu
  // no primeiro teste, com a conta vazia mostrando os cinco.
  const filtrosVisiveis = useMemo(() => {
    const todas = notas || [];
    if (!todas.length) return [];
    const existe = (f: any) => f.key === 'tudo'
      || (f.key === 'foto' ? todas.some((n) => n.photo) : todas.some((n) => n.type === f.key));
    return (FILTERS as any[]).filter(existe);
  }, [notas]);
  const chip = (ativo: boolean): React.CSSProperties => ({
    padding: '7px 13px', borderRadius: 999, whiteSpace: 'nowrap',
    border: `1px solid ${ativo ? 'var(--ember)' : 'var(--line)'}`,
    background: ativo ? 'var(--ember-soft)' : 'transparent',
    color: ativo ? 'var(--ember)' : 'var(--ink-3)',
    fontSize: 13, fontWeight: ativo ? 600 : 500, cursor: 'pointer',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50, background: 'var(--paper)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* ── cabeçalho ─────────────────────────────────────────────── */}
      <div style={{
        padding: 'calc(14px + env(safe-area-inset-top,0px)) 18px 10px',
        borderBottom: '1px solid var(--line)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onBack} aria-label="Voltar" style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--line)', background: 'transparent', cursor: 'pointer',
          }}><Icon name="chevron-left" size={16} color="var(--ink-2)" /></button>

          {buscaAberta ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', borderRadius: 999,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
            }}>
              <Icon name="search" size={15} color="var(--ink-3)" />
              <input
                value={busca} onChange={(e) => setBusca(e.target.value)} autoFocus
                placeholder="Buscar em todas as anotações…"
                style={{
                  flex: 1, border: 'none', background: 'transparent', outline: 'none',
                  color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
                }} />
              <button onClick={() => { setBusca(''); setBuscaAberta(false); }} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              }}><Icon name="x" size={15} color="var(--ink-3)" /></button>
            </div>
          ) : (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="serif" style={{ fontSize: 23, lineHeight: 1.1, color: 'var(--ink)' }}>Estudos</div>
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
                  {total === 0 ? 'nenhuma anotação ainda'
                    : `${total} ${total === 1 ? 'anotação' : 'anotações'} · ${livros.length} ${livros.length === 1 ? 'livro' : 'livros'}`}
                </div>
              </div>
              <button onClick={() => setBuscaAberta(true)} aria-label="Buscar" style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid var(--line)', background: 'transparent', cursor: 'pointer',
              }}><Icon name="search" size={16} color="var(--ink-2)" /></button>
            </>
          )}
        </div>

        {/* filtros: tipo · livro · período */}
        {/* flexWrap, nao overflow-x: arrastar de lado e gesto de TOQUE, e no
            desktop a fileira simplesmente nao rolava — os chips do fim ficavam
            inalcancaveis. Quebrando linha, todos ficam sempre acessiveis. */}
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 12 }}>
          <button onClick={() => setSheetLivros(true)} style={{
            ...chip(livroId !== 'todos'),
            display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 190,
          }}>
            <Icon name="book" size={13} color={livroId !== 'todos' ? 'var(--ember)' : 'var(--ink-3)'} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {livroAtivo ? livroAtivo.title : 'Todos os livros'}
            </span>
          </button>
          {filtrosVisiveis.map((f: any) => (
            <button key={f.key} onClick={() => setFiltro(f.key)} style={chip(filtro === f.key)}>{f.label}</button>
          ))}
          {PERIODOS.filter((p) => p.key !== 'tudo').map((p) => (
            <button key={p.key} onClick={() => setPeriodo(periodo === p.key ? 'tudo' : p.key)}
              style={chip(periodo === p.key)}>{p.label}</button>
          ))}
        </div>
      </div>

      {/* ── lista ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px calc(26px + env(safe-area-inset-bottom,0px))' }}>
        {notas === null ? (
          <div style={{ color: 'var(--ink-3)', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>Carregando…</div>
        ) : lista.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '52px 20px' }}>
            <div className="serif" style={{ fontSize: 21, color: 'var(--ink)', marginBottom: 8 }}>
              {total === 0 ? 'Seu caderno está em branco.'
                : busca ? 'Nada encontrado.' : 'Nenhuma anotação com esses filtros.'}
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.5, maxWidth: 300, margin: '0 auto' }}>
              {total === 0
                ? 'Tudo que você anotar em qualquer livro aparece aqui, junto.'
                : busca ? 'Tente outras palavras ou limpe a busca.' : 'Solte um filtro para ver mais.'}
            </div>
          </div>
        ) : grupos.map((g) => (
          <div key={g.dia} style={{ marginBottom: 22 }}>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--ink-4)', fontWeight: 600, marginBottom: 10,
            }}>{g.dia}</div>

            {g.itens.map((n: any) => (
              <div key={n.id} style={{ marginBottom: 12 }}>
                {/* De qual livro veio — a informação que a tela por livro não
                    precisa dar, e que aqui é essencial. */}
                <button
                  onClick={() => onAbrirLivro && onAbrirLivro(n.bookId, n.page ?? null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5,
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    maxWidth: '100%',
                  }}>
                  <Icon name="book" size={11} color="var(--ink-4)" />
                  <span style={{
                    fontSize: 11.5, color: 'var(--ink-3)', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{n.bookTitle}</span>
                </button>
                <NoteCard
                  n={n}
                  justAdded={false}
                  onEdit={() => { setEditando(n); setRascunho(n.text || ''); }}
                  pageLabel={(p: number | null) => (p == null ? '' : `p. ${p}`)}
                  // Aqui cada nota é de um livro diferente, e ela já vem com o
                  // dele — não precisa cruzar com a lista de livros.
                  livro={{ titulo: n.bookTitle, autor: n.bookAuthor, capaUrl: n.bookCoverUrl }}
                />
              </div>
            ))}
          </div>
        ))}

        {erro && (
          <div style={{ fontSize: 13, color: '#E2574C', textAlign: 'center', padding: '12px 0' }}>{erro}</div>
        )}
      </div>

      {/* ── sheet: escolher livro ─────────────────────────────────── */}
      {sheetLivros && (
        <div onClick={() => setSheetLivros(false)} style={{
          position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.55)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          animation: 'e2FadeIn .2s ease both',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: 'var(--paper)',
            borderTop: '1px solid var(--line-strong)', borderRadius: '24px 24px 0 0',
            padding: '14px 20px 28px', maxHeight: '76vh', overflowY: 'auto',
            animation: 'e2SheetUp .3s cubic-bezier(.22,1,.36,1) both',
          }}>
            <div style={{ width: 34, height: 3.5, borderRadius: 3, background: 'var(--line-strong)', margin: '0 auto 18px' }} />
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--ink-4)', fontWeight: 600, marginBottom: 12,
            }}>filtrar por livro</div>

            <button onClick={() => { setLivroId('todos'); setSheetLivros(false); }} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '13px 0', background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: '1px solid var(--line)',
            }}>
              <span style={{ fontSize: 15, color: livroId === 'todos' ? 'var(--ember)' : 'var(--ink)' }}>Todos os livros</span>
              <span style={{ fontSize: 13, color: 'var(--ink-4)' }}>{total}</span>
            </button>

            {livros.map((b) => (
              <button key={b.id} onClick={() => { setLivroId(b.id); setSheetLivros(false); }} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '13px 0', background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: '1px solid var(--line)', textAlign: 'left',
              }}>
                <span style={{
                  fontSize: 15, color: livroId === b.id ? 'var(--ember)' : 'var(--ink)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{b.title}</span>
                <span style={{ fontSize: 13, color: 'var(--ink-4)', flexShrink: 0 }}>{b.total}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── sheet: editar / apagar ────────────────────────────────── */}
      {editando && (
        <div onClick={() => !salvando && setEditando(null)} style={{
          position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.55)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          animation: 'e2FadeIn .2s ease both',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: 'var(--paper)',
            borderTop: '1px solid var(--line-strong)', borderRadius: '24px 24px 0 0',
            padding: '14px 20px calc(24px + env(safe-area-inset-bottom,0px))',
            animation: 'e2SheetUp .3s cubic-bezier(.22,1,.36,1) both',
          }}>
            <div style={{ width: 34, height: 3.5, borderRadius: 3, background: 'var(--line-strong)', margin: '0 auto 16px' }} />
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'var(--ink-4)', fontWeight: 600, marginBottom: 4,
            }}>{editando.bookTitle}</div>
            <div className="serif" style={{ fontSize: 21, color: 'var(--ink)', marginBottom: 14 }}>Editar anotação</div>

            {/* Foto e áudio não se editam aqui: o compositor deles vive na tela
                do livro, com câmera e gravador. Trazer isso para cá duplicaria
                a peça mais complexa da feature por pouco ganho. */}
            {(editando.photo || editando.audio) ? (
              <div style={{
                padding: '13px 15px', borderRadius: 14, marginBottom: 14,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.5,
              }}>
                Esta anotação tem {editando.photo ? 'foto' : 'áudio'}. Você pode editar a
                legenda aqui — para trocar a mídia, abra no livro.
              </div>
            ) : null}

            <textarea
              ref={areaRef}
              value={rascunho}
              onChange={(e) => setRascunho(e.target.value.slice(0, 5000))}
              rows={5}
              style={{
                width: '100%', padding: '14px 15px', borderRadius: 14, resize: 'none',
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                color: 'var(--ink)', outline: 'none',
                fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 19, lineHeight: 1.45,
              }} />

            <button onClick={salvarEdicao} disabled={salvando} style={{
              width: '100%', marginTop: 14, padding: '15px', borderRadius: 14, border: 'none',
              background: 'var(--ember)', color: '#fff', fontSize: 15.5, fontWeight: 600,
              cursor: salvando ? 'default' : 'pointer', opacity: salvando ? 0.6 : 1,
            }}>{salvando ? 'um instante…' : 'Salvar'}</button>

            <div style={{ display: 'flex', gap: 9, marginTop: 10 }}>
              {onAbrirLivro && (
                <button onClick={() => { const b = editando.bookId, p = editando.page ?? null; setEditando(null); onAbrirLivro(b, p); }}
                  style={{
                    flex: 1, padding: '13px', borderRadius: 999, background: 'transparent',
                    border: '1px solid var(--line-strong)', color: 'var(--ink-2)',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}>Abrir no livro</button>
              )}
              <button onClick={apagar} disabled={salvando} style={{
                flex: 1, padding: '13px', borderRadius: 999, background: 'transparent',
                border: '1px solid rgba(226,87,76,0.4)', color: '#E2574C',
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}>Apagar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstudosGlobalScreen;
